<template>
  <div>
    <paginated-table
      id="profile-loanables-table"
      ref="loanablesTable"
      :label="$t('views.profile.loanables.title')"
      endpoint="loanables"
      :columns="columns"
      :fetch-params="{
        for: 'profile',
      }"
      :load-relations="['active_incidents', 'merged_user_roles.user']"
      :show-action-column="true"
      :extra-filters="extraFilters"
      @row-clicked="(item) => $router.push(`/loanables/${item.id}`)"
    >
      <template #head-buttons>
        <icon-button role="add" to="/profile/loanables/new">{{
          $t("views.profile.loanables.add_button")
        }}</icon-button>
      </template>

      <template #cell(availability_status)="{ item }">
        <availability-status :loanable="item"
      /></template>

      <template #cell(actions)="{ item }">
        <icon-button
          v-if="canEditLoanable(item)"
          role="edit"
          size="sm"
          :to="`/profile/loanables/${item.id}`"
          @click.stop.prevent=""
        />
        <icon-button
          v-if="canDeleteLoanable(item)"
          role="delete"
          size="sm"
          :onclick="async () => disableLoanableModal(item)"
          @click.stop.prevent=""
        />
      </template>
    </paginated-table>

    <template v-if="user.has_fleet">
      <hr />
      <paginated-table
        id="profile-libraries-table"
        ref="libraries-table"
        endpoint="libraries"
        :fetch-params="{
          for: 'profile',
        }"
        :label="$t('titles.libraries') | capitalize"
        :columns="libraries.columns"
        :extra-filters="libraries.extraFilters"
        :show-generate-csv="false"
        sync-filters-with-url
        @row-clicked="(item) => $router.push(`/profile/libraries/${item.id}`)"
      >
        <template #cell(actions)="{ item }">
          <validated-button
            v-if="canDeleteLibrary(item)"
            :action="async () => deleteLibrary(item)"
            :question="$t('views.profile.loanables.libraries.remove.confirm', { name: item.name })"
            :label="$t('forms.remove') | capitalize"
            icon="trash"
            size="sm"
            variant="ghost-danger"
            :show-button-label="false"
          />
        </template>
      </paginated-table>
    </template>
  </div>
</template>

<script>
import AvailabilityStatus from "@/components/Loanable/AvailabilityStatus.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import { capitalize } from "@/helpers/filters";
import {
  canDeleteLoanable,
  canEditLoanable,
  hasLibraryRole,
} from "@/helpers/permissions/loanables";
import Authenticated from "@/mixins/Authenticated";
import { del } from "@/requests/server";

export default {
  name: "ProfileLoanables",
  components: { ValidatedButton, AvailabilityStatus, IconButton, PaginatedTable },
  mixins: [Authenticated],
  data() {
    return {
      libraries: {
        extraFilters: [
          new Filter(
            "sharedInCommunity",
            capitalize(this.$t("communities.fields.community.name")),
            "relation",
            {
              relation: "communities",
              label: "name",
              field: "id",
              params: { for: "admin" },
            }
          ),
        ],

        columns: [
          new Column("id", capitalize(this.$t("lists.id")), "id"),
          new Column("name", capitalize(this.$t("libraries.fields.name")), "text"),
          Column.withRelation(
            "owner_user.full_name",
            capitalize(this.$t("loanables.fields.owner")),
            {
              relation: "users",
              label: "full_name",
              field: "id",
              params: { for: "admin" },
            },
            "owner_user_id",
            {
              sortable: false,
            }
          ),
          Column.withoutFilter(
            "communities_count",
            capitalize(this.$t("views.profile.loanables.libraries.columns.communities_count")),
            "number",
            {
              sortable: false,
            }
          ),
          Column.withoutFilter(
            "loanables_count",
            capitalize(this.$t("views.profile.loanables.libraries.columns.loanables_count")),
            "number",
            {
              sortable: false,
            }
          ),
        ],
      },
      extraFilters: [
        new Filter(
          "sharedInCommunity",
          capitalize(this.$t("communities.fields.community.name")),
          "relation",
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { for: "profile" },
          }
        ),
      ],

      columns: [
        new Column("id", capitalize(this.$t("lists.id")), "id", { showByDefault: false }),
        new Column("name", capitalize(this.$t("loanables.fields.name")), "text"),
        Column.withSelect("type", capitalize(this.$t("loanables.fields.type")), [
          { value: null, label: capitalize(this.$t("loanables.fields.types.all")) },
          { value: "car", label: capitalize(this.$t("loanables.fields.types.car")) },
          { value: "trailer", label: capitalize(this.$t("loanables.fields.types.trailer")) },
          { value: "bike", label: capitalize(this.$t("loanables.fields.types.bike")) },
        ]),
        Column.withRelation(
          "owner_user.full_name",
          capitalize(this.$t("loanables.fields.owner")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
          },
          "owner_user_id",
          {
            sortable: false,
          }
        ),

        Column.withRelation(
          "library.name",
          capitalize(this.$t("views.profile.loanables.columns.fleet")),
          {
            relation: "libraries",
            label: "name",
            field: "id",
          },
          "library_id",
          {
            showByDefault: false,
          }
        ),
        new Column(
          "current_loan.id",
          capitalize(this.$t("views.profile.loanables.columns.current_loan")),
          "text",
          {
            urlFct: (loanable) => `/loans/${loanable.current_loan?.id}`,
            sortable: false,
          }
        ),
        new Column(
          "current_loan.borrower_user.full_name",
          capitalize(this.$t("loans.fields.borrower")),
          "text",
          {
            sortable: false,
          }
        ),
        Column.withSelect(
          "availability_status",
          capitalize(this.$t("loanables.fields.availability_status.title")),
          [
            {
              value: null,
              label: capitalize(this.$t("loanables.fields.availability_status.options.null.all")),
            },
            {
              value: "no_approved_communities",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.no_approved_communities")
              ),
              variant: "warning",
            },
            {
              value: "wrong_type",
              label: capitalize(this.$t("loanables.fields.availability_status.options.wrong_type")),
              variant: "danger",
            },
            {
              value: "no_availabilities",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.no_availabilities")
              ),
              variant: "warning",
            },
            {
              value: "unpublished",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.unpublished")
              ),
              variant: "secondary",
            },
            {
              value: "has_availabilities",
              label: capitalize(
                this.$t("loanables.fields.availability_status.options.has_availabilities")
              ),
              variant: "success",
            },
          ]
        ),
      ],
    };
  },
  computed: {},
  methods: {
    canDeleteLoanable(loanable) {
      return canDeleteLoanable(this.$store.state.user, loanable);
    },
    canEditLoanable(loanable) {
      return canEditLoanable(this.$store.state.user, loanable);
    },
    async disableLoanableModal(loanable) {
      await this.$bvModal
        .msgBoxConfirm("Êtes-vous sûr-e de vouloir retirer ce véhicule de la plateforme?", {
          size: "sm",
          buttonSize: "sm",
          okTitle: "Oui, retirer",
          cancelTitle: "Annuler",
          okVariant: "danger",
          cancelVariant: "ghost-secondary",
          footerClass: "p-2 border-top-0",
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.disableLoanable(loanable);
          }
        });
    },
    async disableLoanable(loanable) {
      await this.$store.dispatch("loanables/disable", loanable.id);
      this.$refs.loanablesTable.refresh();
      this.$store.dispatch("dashboard/loadLoanables");
    },
    canDeleteLibrary(library) {
      return hasLibraryRole(this.$store.state.user, library, "owner");
    },
    async deleteLibrary(library) {
      await del("libraries/" + library.id, {
        notifications: {
          action: this.$t("views.profile.loanables.libraries.remove.action"),
          resourceName: this.$t("views.profile.loanables.libraries.remove.resource"),
          onSuccess: true,
        },
      });
      this.$refs["libraries-table"].refresh();
    },
  },
};
</script>

<style lang="scss">
.profile-loanables__loanables__loanable {
  margin-bottom: 20px;
}
</style>
